import { NumberCellEditor } from '@/components/_SiteWide/Grid/CellEditors/NumberCellEditor'
import { PlusOutlined } from '@ant-design/icons'
import { Horizontal, Texto } from '@gravitate-js/excalibrr'
import { durationFormatter } from '@utils/cell_formatter'
import { getStoreNumberComparator } from '@utils/grid'
import { Tooltip } from 'antd'

function EditableCell() {
  return (
    <Horizontal justifyContent='left' verticalCenter>
      <PlusOutlined style={{ color: 'var(--theme-color-1)' }} />
    </Horizontal>
  )
}

function TimeOverrideRenderer({ value }) {
  if (value) {
    return (
      <div>
        <span className='dispatch-window-tag in-window'>{durationFormatter(value)}</span>
      </div>
    )
  }
  return <EditableCell />
}

function PayrollOverrideRenderer({ value }) {
  if (value) {
    return (
      <div>
        <span className='dispatch-window-tag in-window'>{value}m</span>
      </div>
    )
  }
  return <EditableCell />
}

function DistanceOverrideRenderer({ value, formatDistance }) {
  if (value) {
    return (
      <div>
        <span className='dispatch-window-tag in-window'>{formatDistance(value)}</span>
      </div>
    )
  }
  return <EditableCell />
}

function DistanceRenderer({ params, formatDistance }) {
  if (params.node.group) return
  if (!params.value) return <APINotCalculatedRenderer />
  return (
    <Texto appearance={params.data?.distance_override ? 'medium' : 'default'}>{formatDistance(params.value)}</Texto>
  )
}

function TimeRenderer({ params }) {
  if (params.node.group) return
  if (!params.value) return <APINotCalculatedRenderer />
  return <Texto>{durationFormatter(params.value)}</Texto>
}

function APINotCalculatedRenderer() {
  return (
    <Tooltip title='Route Estimate from API will not be calculated until route is utilized for creating an order or running a model.'>
      <div>
        <Texto>Pending</Texto>
      </div>
    </Tooltip>
  )
}

export const getColumnDefs = (formatDistance) => {
  const routeInfoColumns = [
    {
      headerName: 'Origin',
      field: 'origin_location_name',
      editable: false,
      comparator: getStoreNumberComparator('origin_location_name'),
    },
    {
      headerName: 'Origin Type',
      field: 'origin_location_type',
      editable: false,
      initialHide: true,
    },
    {
      headerName: 'Origin Lat.',
      field: 'origin_lat',
      enableRowGroup: false,
      editable: false,
      initialHide: true,
      filter: 'agNumberColumnFilter',
    },
    {
      headerName: 'Origin Lon.',
      field: 'origin_lon',
      enableRowGroup: false,
      editable: false,
      initialHide: true,
      filter: 'agNumberColumnFilter',
    },
    {
      headerName: 'Dest.',
      field: 'destination_location_name',
      editable: false,
      comparator: getStoreNumberComparator('destination_location_name'),
    },
    {
      headerName: 'Dest. Type',
      field: 'destination_location_type',
      editable: false,
      initialHide: true,
    },
    {
      headerName: 'Dest. Lat.',
      field: 'destination_lat',
      editable: false,
      enableRowGroup: false,
      initialHide: true,
      filter: 'agNumberColumnFilter',
    },
    {
      headerName: 'Dest. Lon.',
      field: 'destination_lon',
      editable: false,
      enableRowGroup: false,
      initialHide: true,
      filter: 'agNumberColumnFilter',
    },
  ]
  const routeTimeColumns = [
    {
      headerName: 'Time',
      headerTooltip: 'Calculated time',
      field: 'api_travel_time_minutes',
      editable: false,
      cellRenderer: (params) => <TimeRenderer params={params} />,
      filter: 'agNumberColumnFilter',
      enableRowGroup: false,
      filterParams: {
        filterPlaceholder: 'Time Minutes',
      },
    },
    {
      headerName: 'Time Override',
      headerTooltip: 'Overrides the time when this route is used in projections. Enter override in minutes.',
      field: 'override_travel_time_minutes',
      editable: true,
      enableRowGroup: false,
      cellRenderer: (params) => (!params.node.group ? <TimeOverrideRenderer value={params.value} /> : null),
      filter: 'agNumberColumnFilter',
      filterParams: {
        filterPlaceholder: 'Time Minutes',
      },
    },
    {
      headerName: 'Payroll Override',
      headerTooltip: 'Overrides the time used for payroll calculations. Enter override in minutes',
      field: 'override_payroll_minutes',
      cellEditor: NumberCellEditor,
      editable: true,
      enableRowGroup: false,
      cellRenderer: (params) => (!params.node.group ? <PayrollOverrideRenderer value={params.value} /> : null),
      filter: 'agNumberColumnFilter',
      filterParams: {
        filterPlaceholder: 'Time Minutes',
      },
    },
  ]
  const routeDistanceColumns = [
    {
      headerName: 'Distance',
      headerTooltip: 'Calculated distance',
      field: 'api_distance_miles',
      filter: 'agNumberColumnFilter',
      editable: false,
      enableRowGroup: false,
      cellRenderer: (params) => DistanceRenderer({ params, formatDistance }),
    },
    {
      headerName: 'Straight Line Distance',
      headerTooltip: 'Distance calculated using Haversine formula (straight line)',
      field: 'haversine_distance_miles',
      editable: false,
      filter: 'agNumberColumnFilter',
      enableRowGroup: false,
      cellRenderer: (params) => DistanceRenderer({ params, formatDistance }),
      initialHide: true,
    },

    {
      headerName: 'Distance Override',
      headerTooltip:
        'Overrides the distance when this route is used in calculations / projections. Superseded by Point To Point overrides',
      field: 'override_distance_miles',
      editable: true,
      enableRowGroup: false,
      filter: 'agNumberColumnFilter',
      cellRenderer: (params) =>
        !params.node.group ? <DistanceOverrideRenderer value={params.value} formatDistance={formatDistance} /> : null,
    },
    {
      headerName: 'Payroll Override',
      headerTooltip: 'Overrides the deemed mileage for the route when calculating payroll',
      field: 'override_payroll_miles',
      filter: 'agNumberColumnFilter',
      editable: true,
      enableRowGroup: false,
      cellRenderer: (params) =>
        !params.node.group ? <DistanceOverrideRenderer value={params.value} formatDistance={formatDistance} /> : null,
    },
  ]

  return [
    { headerName: 'Route Info', marryChildren: true, children: routeInfoColumns },
    {
      headerName: 'Route Time',
      marryChildren: true,
      children: routeTimeColumns,
    },
    {
      headerName: 'Route Distance',
      marryChildren: true,
      children: routeDistanceColumns,
    },
  ]
}
